import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);

    // Handle custom click events from HTML
    this.addEventListener('click', (event) => {
      if (event.target.closest('[on\\:click="/openSubmenu"]')) {
        this.openSubmenu(event);
      } else if (event.target.closest('[on\\:click="/backFromSubmenu"]')) {
        this.backFromSubmenu(event);
      } else if (event.target.closest('[on\\:click="/close"]')) {
        this.close();
      } else if (event.target.closest('[on\\:click="/toggle"]')) {
        this.toggle();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the main drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));
    trapFocus(details);
  }

  /**
   * Close the main drawer
   */
  close() {
    const details = this.refs.details;
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      // Remove focus trap
      removeTrapFocus();

      // Reset all submenus: remove class + hide
      const submenus = this.querySelectorAll('.menu-drawer__body-submenu');
      const menuDrawer = this.querySelector('.menu-drawer');
      submenus.forEach((submenu) => {
        submenu.classList.remove('submenu-open', 'submenu-prepare', 'active');
        submenu.style.display = 'none'; // 👈 this is the key fix
        menuDrawer.style.overflow = 'scroll';
      });

      // Reset all open <details> elements
      const openDetails = this.querySelectorAll('details[open]');
      openDetails.forEach(reset);
    });
  }

  /**
   * Go back from a submenu
   * @param {Event} event
   */
  backFromSubmenu(event) {
    const submenu = event.target.closest('.menu-drawer__body-submenu');
    const menuDrawer = this.querySelector('.menu-drawer');
    if (!submenu) return;

    submenu.classList.remove('submenu-open', 'active', 'submenu-prepare');
    menuDrawer.style.overflow = 'scroll';

    setTimeout(() => {
      submenu.style.display = 'none';
    }, 300); // match transition duration

    trapFocus(this.refs.details);
  }

  /**
   * Open a submenu
   * @param {Event} event
   */
  openSubmenu(event) {
    event.preventDefault();

    const link = event.target.closest('a, button');
    const listItem = link?.closest('.menu-drawer__body-menu-list-item--child');

    if (!listItem) {
      console.warn('List item not found');
      return;
    }

    const submenu = listItem.querySelector('.menu-drawer__body-submenu');

    if (!submenu) {
      console.warn('Submenu not found');
      return;
    }

    // Close all other submenus (remove class from submenu element)
    const allSubmenus = this.querySelectorAll('.menu-drawer__body-submenu');
    const menuDrawer = this.querySelector('.menu-drawer');
    allSubmenus.forEach((sm) => {
      sm.classList.remove('submenu-open', 'submenu-prepare', 'active');
      sm.style.display = 'none';
      menuDrawer.style.overflow = 'hidden';
    });

    // Show & prepare this submenu
    submenu.classList.add('submenu-prepare');
    submenu.style.display = 'block';

    // Delay to trigger transition
    requestAnimationFrame(() => {
      setTimeout(() => {
        submenu.classList.add('submenu-open', 'active');
      }, 20);
    });

    // Optional: trap focus in submenu
    trapFocus(submenu);
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

class MenuDrawerNewsletter extends HTMLElement {
  constructor() {
    super();
    this.submitBtn = this.querySelector('form button');
    this.tempBtn = this.querySelector('.menu-drawer__body-newsletter-form--tempBtn');
    if (this.submitBtn && this.submitBtn.hasAttribute('disabled')) {
      this.submitBtn.classList.add('hidden');
      this.tempBtn?.classList.remove('hidden');

      setTimeout(() => {
        document.documentElement.removeAttribute('scroll-lock');
      }, 300);

      setTimeout(() => {
        this.submitBtn?.classList.remove('hidden');
        this.tempBtn?.classList.add('hidden');
        this.submitBtn?.removeAttribute('disabled');
      }, 10000);
    }
  }
}
customElements.define('menu-drawer-newsletter', MenuDrawerNewsletter);
